import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, DollarSign, Zap, AlertTriangle, RefreshCw, Activity, Loader2, Settings, Save,
} from 'lucide-react';
import { adminGet, adminPut } from '../data/api';
import LLMAutoRunPanel from '../components/LLMAutoRunPanel';

const mono = { fontFamily: 'JetBrains Mono, monospace' };
const heading = { fontFamily: 'Outfit, sans-serif' };
const card = { background: '#18181B', border: '1px solid #27272A' };

function fmtUsd(v) {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toFixed(2)}`;
}
function fmtCredits(v) {
  if (v === null || v === undefined) return '—';
  return `${Number(v).toFixed(2)}`;
}
function fmtK(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toFixed(0);
}

function StatCard({ label, value, sublabel, icon: Icon, testid, color = 'emerald' }) {
  return (
    <div data-testid={testid} className="rounded-sm p-4" style={card}>
      <div className="flex items-start justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-zinc-500" style={heading}>
          {label}
        </div>
        {Icon && <Icon size={14} className={`text-${color}-400`} />}
      </div>
      <div className="text-2xl font-bold text-zinc-100" style={mono}>
        {value}
      </div>
      {sublabel && (
        <div className="text-xs text-zinc-500 mt-1" style={mono}>{sublabel}</div>
      )}
    </div>
  );
}

export default function AdminLLMCosts() {
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [budget, setBudget] = useState(null);
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(30);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [s, r, b, st] = await Promise.all([
        adminGet(`/tt/admin/llm-costs/summary?days=${days}`),
        adminGet('/tt/admin/llm-costs/recent?limit=50'),
        adminGet('/tt/admin/llm-costs/budget-status'),
        adminGet('/tt/admin/llm-costs/settings'),
      ]);
      setSummary(s);
      setRecent(r?.items || []);
      setBudget(b);
      setSettings(st);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const crits = typeof settings.critical_endpoints === 'string'
        ? settings.critical_endpoints.split(',').map(s => s.trim()).filter(Boolean)
        : settings.critical_endpoints;
      const payload = {
        daily_budget_warn_usd: Number(settings.daily_budget_warn_usd),
        weekly_endpoint_warn_usd: Number(settings.weekly_endpoint_warn_usd),
        error_rate_warn: Math.round(Number(settings.error_rate_warn)),
        cap_fraction: Math.max(0, Math.min(1, Number(settings.cap_fraction))),
        critical_endpoints: crits,
      };
      await adminPut('/tt/admin/llm-costs/settings', undefined, payload);
      setSaveMsg('✓ Impostazioni salvate');
      await load();  // refresh budget-status
    } catch (e) {
      setSaveMsg(`✗ Errore: ${e.message || e}`);
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 4000);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const todayCredits = summary?.today?.credits ?? 0;
  const last7Credits = summary?.last_7d?.credits ?? 0;
  const last30Credits = summary?.last_30d?.credits ?? 0;
  const yesterdayCredits = summary?.yesterday?.credits ?? 0;
  const dailyMax = useMemo(() => {
    const xs = (summary?.daily || []).map(d => d.credits || 0);
    return xs.length ? Math.max(...xs, 0.01) : 0.01;
  }, [summary]);

  return (
    <div className="min-h-screen bg-[#0D0D0F] text-zinc-200 px-4 py-6 max-w-[1400px] mx-auto" data-testid="admin-llm-costs-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/admin" className="text-zinc-500 hover:text-zinc-300" data-testid="back-to-admin">
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-zinc-100" style={heading}>LLM Costs</h1>
            <p className="text-xs text-zinc-500">
              Spesa stimata delle chiamate Claude/OpenAI/Gemini tracciate
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="llm-costs-days-select"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-[#18181B] border border-[#27272A] rounded-sm text-xs px-2 py-1.5 text-zinc-200"
          >
            <option value={7}>Ultimi 7 giorni</option>
            <option value={14}>Ultimi 14 giorni</option>
            <option value={30}>Ultimi 30 giorni</option>
            <option value={60}>Ultimi 60 giorni</option>
            <option value={90}>Ultimi 90 giorni</option>
          </select>
          <button
            onClick={load}
            disabled={loading}
            data-testid="llm-costs-refresh-btn"
            className="flex items-center gap-1 bg-[#18181B] border border-[#27272A] rounded-sm text-xs px-3 py-1.5 hover:bg-[#27272A]"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Aggiorna
          </button>
        </div>
      </div>

      {err && (
        <div className="rounded-sm p-3 mb-4 text-xs text-red-300 flex items-center gap-2" style={{ background: '#2D1416', border: '1px solid #4B1F22' }} data-testid="llm-costs-error">
          <AlertTriangle size={14} /> {err}
        </div>
      )}

      {budget && (
        <div
          className="rounded-sm p-3 mb-4 text-xs flex items-center gap-3"
          style={budget.critical_only_mode
            ? { background: '#2D1416', border: '1px solid #7F1D1D', color: '#FCA5A5' }
            : { background: '#0F1F14', border: '1px solid #14532D', color: '#86EFAC' }}
          data-testid="budget-status-banner"
        >
          {budget.critical_only_mode ? <AlertTriangle size={14} /> : <Zap size={14} />}
          <div className="flex-1" style={mono}>
            {budget.critical_only_mode ? (
              <>
                <b>CRITICAL-ONLY MODE</b> — Oggi {budget.today_spend_usd} credits
                spesi (≥ {budget.throttle_threshold_usd} cap = {(budget.cap_fraction * 100).toFixed(0)}%
                di {budget.daily_budget_warn_usd}). Endpoint non critici auto-skippati.
                Critical: {budget.critical_endpoints.join(', ')}.
              </>
            ) : (
              <>
                Budget OK — Oggi {budget.today_spend_usd}/{budget.throttle_threshold_usd} credits
                ({((budget.today_spend_usd / Math.max(budget.throttle_threshold_usd, 0.01)) * 100).toFixed(0)}%
                della soglia throttle). Critical-only mode si attiva a
                {' '}{budget.throttle_threshold_usd} credits.
              </>
            )}
          </div>
          <button
            onClick={() => setSettingsOpen(v => !v)}
            data-testid="toggle-settings-btn"
            className="flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] hover:bg-black/30"
            style={{ border: '1px solid currentColor' }}
          >
            <Settings size={12} /> {settingsOpen ? 'Chiudi' : 'Impostazioni'}
          </button>
        </div>
      )}

      {settingsOpen && settings && (
        <div className="rounded-sm p-4 mb-4" style={card} data-testid="budget-settings-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider text-zinc-500" style={heading}>
              Impostazioni Budget
            </div>
            {saveMsg && (
              <div
                className={`text-xs ${saveMsg.startsWith('✓') ? 'text-emerald-300' : 'text-red-300'}`}
                style={mono}
              >
                {saveMsg}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs" style={mono}>
            <label className="flex flex-col gap-1">
              <span className="text-zinc-400">Daily budget warn (credits)</span>
              <input
                type="number" step="0.5" min="0.01"
                data-testid="input-daily-budget-warn"
                value={settings.daily_budget_warn_usd}
                onChange={e => setSettings({ ...settings, daily_budget_warn_usd: e.target.value })}
                className="bg-[#0D0D0F] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200"
              />
              <span className="text-zinc-600 text-[10px]">
                Alert Telegram se la spesa di oggi supera questa soglia.
              </span>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-zinc-400">Cap fraction (0–1)</span>
              <input
                type="number" step="0.05" min="0" max="1"
                data-testid="input-cap-fraction"
                value={settings.cap_fraction}
                onChange={e => setSettings({ ...settings, cap_fraction: e.target.value })}
                className="bg-[#0D0D0F] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200"
              />
              <span className="text-zinc-600 text-[10px]">
                Critical-only mode scatta a daily_budget × cap_fraction ({(Number(settings.cap_fraction) * Number(settings.daily_budget_warn_usd)).toFixed(2)} credits).
              </span>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-zinc-400">Weekly endpoint warn (credits)</span>
              <input
                type="number" step="5" min="1"
                data-testid="input-weekly-endpoint-warn"
                value={settings.weekly_endpoint_warn_usd}
                onChange={e => setSettings({ ...settings, weekly_endpoint_warn_usd: e.target.value })}
                className="bg-[#0D0D0F] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200"
              />
              <span className="text-zinc-600 text-[10px]">
                Alert se un singolo endpoint sfora questa soglia in 7gg.
              </span>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-zinc-400">Error-rate warn (errori/ora)</span>
              <input
                type="number" step="1" min="1"
                data-testid="input-error-rate-warn"
                value={settings.error_rate_warn}
                onChange={e => setSettings({ ...settings, error_rate_warn: e.target.value })}
                className="bg-[#0D0D0F] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200"
              />
              <span className="text-zinc-600 text-[10px]">
                Alert se errori LLM nell'ultima ora superano questa soglia (es. balance esaurita).
              </span>
            </label>
            <label className="flex flex-col gap-1 md:col-span-2">
              <span className="text-zinc-400">Critical endpoints (virgola-separati, MAI skippati)</span>
              <input
                type="text"
                data-testid="input-critical-endpoints"
                value={Array.isArray(settings.critical_endpoints) ? settings.critical_endpoints.join(', ') : settings.critical_endpoints}
                onChange={e => setSettings({ ...settings, critical_endpoints: e.target.value })}
                className="bg-[#0D0D0F] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200"
              />
              <span className="text-zinc-600 text-[10px]">
                Es: <code>match_analysis, system_review</code>. Gli endpoint listati bypassano il throttle anche oltre il cap.
              </span>
            </label>
          </div>
          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              onClick={load}
              data-testid="settings-reload-btn"
              className="text-xs px-3 py-1.5 rounded-sm border border-[#27272A] text-zinc-400 hover:text-zinc-200"
            >
              Ripristina
            </button>
            <button
              onClick={saveSettings}
              disabled={saving}
              data-testid="settings-save-btn"
              className="flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-600 text-white text-xs px-3 py-1.5 rounded-sm disabled:opacity-50"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Salva
            </button>
          </div>
        </div>
      )}

      <div className="mb-6">
        <LLMAutoRunPanel />
      </div>

      {summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatCard
              testid="stat-today"
              label="Oggi"
              value={fmtCredits(todayCredits)}
              sublabel={`${summary?.today?.calls || 0} calls`}
              icon={Zap}
            />
            <StatCard
              testid="stat-yesterday"
              label="Ieri"
              value={fmtCredits(yesterdayCredits)}
              sublabel={`${summary?.yesterday?.calls || 0} calls`}
              icon={Activity}
              color="zinc"
            />
            <StatCard
              testid="stat-7d"
              label="Ultimi 7 gg"
              value={fmtCredits(last7Credits)}
              sublabel={`${summary?.last_7d?.calls || 0} calls • avg ${fmtCredits(last7Credits / 7)}/gg`}
              icon={DollarSign}
              color="amber"
            />
            <StatCard
              testid="stat-30d"
              label="Ultimi 30 gg"
              value={fmtCredits(last30Credits)}
              sublabel={`${summary?.last_30d?.calls || 0} calls • avg ${fmtCredits(last30Credits / 30)}/gg`}
              icon={DollarSign}
              color="rose"
            />
          </div>

          {/* Daily timeline */}
          <div className="rounded-sm p-4 mb-6" style={card} data-testid="daily-timeline">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-wider text-zinc-500" style={heading}>
                Spesa giornaliera ({summary?.window_days}gg)
              </div>
              <div className="text-xs text-zinc-600" style={mono}>
                max {fmtCredits(dailyMax)}
              </div>
            </div>
            <div className="flex items-end gap-[2px] h-28">
              {(summary?.daily || []).map(d => {
                const pct = (d.credits / dailyMax) * 100;
                const isToday = d.date === new Date().toLocaleDateString('it-IT').split('/').reverse().join('-');
                return (
                  <div
                    key={d.date}
                    className="flex-1 min-w-[4px] rounded-sm transition-colors hover:bg-emerald-300"
                    style={{
                      height: `${Math.max(pct, 2)}%`,
                      background: isToday ? '#10B981' : '#3F3F46',
                    }}
                    title={`${d.date}: ${fmtCredits(d.credits)} (${d.calls} calls)`}
                    data-testid={`daily-bar-${d.date}`}
                  />
                );
              })}
            </div>
            {!summary?.daily?.length && (
              <div className="text-center text-zinc-500 text-xs py-8">
                Nessuna chiamata LLM tracciata. Tracking attivo da ora.
              </div>
            )}
          </div>

          {/* Per-endpoint breakdown */}
          <div className="rounded-sm mb-6" style={card} data-testid="per-endpoint-table">
            <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-zinc-500" style={heading}>
                Breakdown per endpoint ({summary?.window_days}gg)
              </div>
              <div className="text-xs text-zinc-600">Top 20 per costo</div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-zinc-500 uppercase">
                  <th className="text-left px-4 py-2 font-medium">Endpoint</th>
                  <th className="text-right px-4 py-2 font-medium">Calls</th>
                  <th className="text-right px-4 py-2 font-medium">Input tok</th>
                  <th className="text-right px-4 py-2 font-medium">Output tok</th>
                  <th className="text-right px-4 py-2 font-medium">Credits</th>
                  <th className="text-right px-4 py-2 font-medium">% del totale</th>
                </tr>
              </thead>
              <tbody style={mono}>
                {(summary?.per_endpoint || []).map((e) => {
                  const pct = last30Credits ? (e.credits / last30Credits) * 100 : 0;
                  return (
                    <tr key={e.endpoint} className="border-t border-[#27272A]/60 text-zinc-300 hover:bg-[#1F1F22]">
                      <td className="px-4 py-2">{e.endpoint}</td>
                      <td className="text-right px-4 py-2">{e.calls}</td>
                      <td className="text-right px-4 py-2 text-zinc-500">{fmtK(e.prompt_tokens)}</td>
                      <td className="text-right px-4 py-2 text-zinc-500">{fmtK(e.completion_tokens)}</td>
                      <td className="text-right px-4 py-2 text-emerald-300">{fmtCredits(e.credits)}</td>
                      <td className="text-right px-4 py-2 text-zinc-500">{pct.toFixed(1)}%</td>
                    </tr>
                  );
                })}
                {!summary?.per_endpoint?.length && (
                  <tr>
                    <td colSpan={6} className="text-center text-zinc-500 py-8 text-xs">
                      Nessun dato ancora. Il tracker registra dalla prossima chiamata LLM.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Recent calls */}
          <div className="rounded-sm" style={card} data-testid="recent-calls-table">
            <div className="px-4 py-3 border-b border-[#27272A] text-xs uppercase tracking-wider text-zinc-500" style={heading}>
              Ultime 50 chiamate
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-zinc-500 uppercase">
                    <th className="text-left px-4 py-2 font-medium">Ora</th>
                    <th className="text-left px-4 py-2 font-medium">Endpoint</th>
                    <th className="text-left px-4 py-2 font-medium">Match</th>
                    <th className="text-right px-4 py-2 font-medium">In tok</th>
                    <th className="text-right px-4 py-2 font-medium">Out tok</th>
                    <th className="text-right px-4 py-2 font-medium">Credits</th>
                    <th className="text-left px-4 py-2 font-medium">Errore</th>
                  </tr>
                </thead>
                <tbody style={mono}>
                  {recent.map((r, idx) => (
                    <tr key={idx} className="border-t border-[#27272A]/60 text-zinc-300 hover:bg-[#1F1F22]">
                      <td className="px-4 py-2 text-zinc-500">{(r.ts || '').replace('T', ' ').replace('Z', '')}</td>
                      <td className="px-4 py-2">{r.endpoint}</td>
                      <td className="px-4 py-2 text-zinc-500 truncate max-w-[280px]" title={r.match_id || ''}>
                        {(r.match_id || '—').slice(0, 35)}
                      </td>
                      <td className="text-right px-4 py-2 text-zinc-500">{fmtK(r.prompt_tokens)}</td>
                      <td className="text-right px-4 py-2 text-zinc-500">{fmtK(r.completion_tokens)}</td>
                      <td className={`text-right px-4 py-2 ${r.error ? 'text-zinc-600' : 'text-emerald-300'}`}>
                        {fmtCredits(r.credits)}
                      </td>
                      <td className="px-4 py-2 text-red-400 text-xs truncate max-w-[200px]" title={r.error || ''}>
                        {r.error ? r.error.slice(0, 40) : ''}
                      </td>
                    </tr>
                  ))}
                  {!recent.length && (
                    <tr>
                      <td colSpan={7} className="text-center text-zinc-500 py-8">
                        Nessuna chiamata recente.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-xs text-zinc-600 mt-4" style={mono}>
            {summary?.note}
          </div>
        </>
      )}
    </div>
  );
}
